<template>
  <v-sheet class="pa-5">

    <!-- Loading state -->
    <div
      v-if="loading"
      class="d-flex justify-center align-center"
      style="min-height: 200px;"
    >
      <v-progress-circular
        color="primary"
        indeterminate
      />
    </div>

    <!-- Group Detail -->
    <template v-else-if="familyGroup">

      <!-- Group Header Section -->
      <div class="d-flex align-center ga-2 mb-6">

        <!-- Inline Editing Mode (Only for Owner) -->
        <template v-if="editingName && isOwner">
          <v-text-field
            v-model="editedName"
            autofocus
            density="compact"
            hide-details
            variant="outlined"
            @keyup.enter="saveName"
            @keyup.escape="cancelEditName"
          />

          <v-btn
            color="primary"
            icon="mdi-check"
            size="small"
            variant="text"
            @click="saveName"
          />

          <v-btn
            icon="mdi-close"
            size="small"
            variant="text"
            @click="cancelEditName"
          />
        </template>

        <!-- Display Mode -->
        <template v-else>
          <h2>
            {{ familyGroup.name }}
          </h2>

          <!-- Owner Actions -->
          <template v-if="isOwner">
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              @click="startEditName"
            />

            <v-btn
              color="error"
              icon="mdi-delete"
              size="small"
              variant="text"
              @click="showDeleteDialog = true"
            />
          </template>

        </template>
      </div>

      <v-divider class="mb-6" />

      <!-- Members Management Section -->
      <div class="d-flex align-center mb-4">
        <h3 class="text-h6">
          Membres du groupe
        </h3>

        <v-spacer />

        <v-chip
          color="primary"
          size="small"
          variant="outlined"
        >
          {{ allMembers.length }} membre(s)
        </v-chip>
      </div>

      <v-table class="border rounded">
        <thead>
          <tr>
            <th class="text-left">
              Email
            </th>

            <th
              v-if="isOwner"
              class="text-right"
              style="width: 100px;"
            >
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          <!-- Inline Add Member Row (Only for Owner) -->
          <tr
            v-if="isOwner"
            class="bg-grey-lighten-5"
          >
            <td>
              <v-text-field
                v-model="newMemberEmail"
                density="compact"
                :disabled="addingMember"
                hide-details
                placeholder="Ajouter l'email d'un membre..."
                prepend-inner-icon="mdi-email-plus"
                variant="plain"
                @keyup.enter="tryAddMember"
              />
            </td>

            <td class="text-right">
              <v-btn
                color="primary"
                :disabled="!newMemberEmail"
                icon="mdi-plus"
                :loading="addingMember"
                size="small"
                variant="text"
                @click="tryAddMember"
              />
            </td>
          </tr>

          <!-- Members List -->
          <tr
            v-for="member in allMembers"
            :key="member.id"
          >
            <td>
              {{ member.email }}
              <v-chip
                v-if="authStore.getUserId === member.id"
                color="secondary"
                size="x-small"
                variant="tonal"
              >
                Vous
              </v-chip>
              <v-chip
                v-if="familyGroup.owner_id === member.id"
                color="secondary"
                size="x-small"
                variant="tonal"
              >
                Propriétaire du groupe
              </v-chip>
            </td>

            <td
              v-if="isOwner"
              class="text-right"
            >
              <v-btn
                v-if="familyGroup.owner_id !== member.id"
                color="error"
                icon="mdi-delete-outline"
                size="small"
                variant="text"
                @click="tryRemoveMember(member.email)"
              />
            </td>
          </tr>

          <!-- Empty State for Members -->
          <tr v-if="allMembers.length === 0">
            <td
              class="text-center text-grey pa-4"
              colspan="2"
            >
              Aucun membre dans ce groupe
            </td>
          </tr>
        </tbody>
      </v-table>
    </template>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-if="isOwner"
      v-model="showDeleteDialog"
      max-width="450"
    >
      <v-card>
        <v-card-title class="text-h5">
          Supprimer le groupe ?
        </v-card-title>

        <v-card-text>
          Êtes-vous sûr de vouloir supprimer le groupe <strong>{{ familyGroup?.name }}</strong> ?
          Cette action est irréversible.
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="deleting"
            variant="text"
            @click="showDeleteDialog = false"
          >
            Annuler
          </v-btn>

          <v-btn
            color="error"
            :loading="deleting"
            variant="elevated"
            @click="confirmDelete"
          >
            Supprimer définitivement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script setup lang="ts">
  import type { FamilyGroup } from '@/model/family-group.ts'
  import { computed, onMounted, ref } from 'vue'
  import { familyGroupRepository } from '@/api/repository/familygroup.repository.ts'
  import { useAuthStore } from '@/stores/auth.ts'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  // --- Props & Emits ---

  const props = defineProps<{
    familyGroupId: string
  }>()

  const emit = defineEmits<{
    'update:familyGroup': [familyGroup: FamilyGroup]
    'deleted': []
  }>()

  // --- Services ---

  const authStore = useAuthStore()
  const toastStore = useToastStore()

  // --- State ---

  const familyGroup = ref<FamilyGroup | null>(null)

  const loading = ref(true)

  const editingName = ref(false)

  const editedName = ref('')

  const showDeleteDialog = ref(false)

  const deleting = ref(false)

  const newMemberEmail = ref('')

  const addingMember = ref(false)

  // --- Computed ---

  /**
   * Check if the current authenticated user is the group owner
   */
  const isOwner = computed(() => {
    if (!familyGroup.value) {
      return false
    }

    return familyGroup.value.owner_id === authStore.getUserId
  })

  const allMembers = computed(() => {
    if (!familyGroup.value?.members_details) {
      return []
    }

    return familyGroup.value.members_details
  })

  // --- Methods ---

  /**
   * Fetch group details from repository
   */
  async function loadFamilyGroup () {
    loading.value = true

    try {
      familyGroup.value = await familyGroupRepository.getFamilyGroup(props.familyGroupId)
    } catch (error) {
      notifyApiError('Impossible de charger le groupe', error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Initialize and show the inline name editor
   */
  function startEditName () {
    if (!isOwner.value) {
      return
    }

    editedName.value = familyGroup.value?.name ?? ''
    editingName.value = true
  }

  /**
   * Hide the inline name editor without saving
   */
  function cancelEditName () {
    editingName.value = false
  }

  /**
   * Persist name change to backend
   */
  async function saveName () {
    const trimmed = editedName.value.trim()

    if (!trimmed || !familyGroup.value || trimmed === familyGroup.value.name) {
      cancelEditName()
      return
    }

    try {
      const updated = await familyGroupRepository.updateFamilyGroup(props.familyGroupId, { name: trimmed })

      familyGroup.value = updated
      editingName.value = false

      emit('update:familyGroup', updated)
      toastStore.notify('Groupe renommé', ToastType.Success)
    } catch (error) {
      notifyApiError('Erreur lors du renommage du groupe', error)
    }
  }

  /**
   * Delete the group via repository
   */
  async function confirmDelete () {
    if (!isOwner.value) {
      return
    }

    deleting.value = true

    try {
      await familyGroupRepository.deleteFamilyGroup(props.familyGroupId)

      toastStore.notify('Groupe supprimé', ToastType.Success)
      showDeleteDialog.value = false

      emit('deleted')
    } catch (error) {
      notifyApiError('Erreur lors de la suppression du groupe', error)
    } finally {
      deleting.value = false
    }
  }

  /**
   * Add a new member to the group
   */
  async function tryAddMember () {
    const email = newMemberEmail.value.trim()

    if (!email || !isOwner.value) {
      return
    }

    addingMember.value = true

    try {
      await familyGroupRepository.addMember(props.familyGroupId, email)

      toastStore.notify('Membre ajouté', ToastType.Success)
      newMemberEmail.value = ''

      await loadFamilyGroup()
    } catch (error) {
      notifyApiError('Erreur lors de l\'ajout du membre', error)
    } finally {
      addingMember.value = false
    }
  }

  /**
   * Remove a member from the group
   */
  async function tryRemoveMember (email: string) {
    if (!isOwner.value) {
      return
    }

    try {
      await familyGroupRepository.removeMember(props.familyGroupId, email)

      toastStore.notify('Membre retiré', ToastType.Success)

      await loadFamilyGroup()
    } catch (error) {
      notifyApiError('Erreur lors de la suppression du membre', error)
    }
  }

  // --- Lifecycle ---

  onMounted(() => {
    loadFamilyGroup()
  })
</script>

<style scoped>
/* Lighten the table header for better visual hierarchy */
.v-table :deep(thead) {
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}

:deep(.v-card-title) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
